
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select'
import { PanelComponent } from '@components/core/panel/panel.component';
import { CertProService } from '@services/core/certpro.service';
import { EntityService } from '@services/core/entity.service';
import { NgbModal, NgbNavModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-certpro',
  templateUrl: './certpro.component.html',
  styleUrls: ["./certpro.component.scss"],
  imports: [PanelComponent, DragDropModule, FormsModule, ReactiveFormsModule, NgbNavModule, NgbTooltipModule, NgSelectComponent, NgbTooltipModule],
  providers: [CertProService, EntityService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class CertProTemplateComponent implements OnInit {
  protected categories
  protected questions
  protected selectCategoryId
  protected showPreview = false
  protected preview

  private certProService = inject(CertProService)
  private entityService = inject(EntityService)
  protected certProTypes
  protected typeCertPro
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)
  private destroyRef = inject(DestroyRef)
  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: null
  }
  public closeResult = ""

  protected yearSelected
  protected years = []
  protected newCategoryForm = this.fb.group({
    id: [null],
    groupId: ["", Validators.required],
    year: [""],
    name: ["", Validators.required],
    tooltip: [""],
    enabled: [true]
  })

  protected newQuestionForm = this.fb.group({
    id: [null],
    categoryId: [""],
    title: ["", Validators.required],
    tooltip: [""],
    year: [""],
    subitems: [[]],
    enabled: [true]
  })

  protected newSubQuestionForm = this.fb.group({
    id: [null],
    parentId: ["", Validators.required],
    categoryId: ["", Validators.required],
    title: ["", Validators.required],
    tooltip: [""],
    year: [""],
    enabled: [true]
  })




  ngOnInit(): void {
    this.categories = []
    this.setYears(2024)
    this.getCertProTypes()
  }

  setYears(initial) {
    const today = new Date()
    const year = today.getFullYear()
    this.yearSelected = year
    for (let i = year; i >= initial; i--) {
      this.years.push(i)
    }
  }

  open(modal) {
    this.modalService.open(modal, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `${reason}`;
    });
  }



  getCategories() {
    this.certProService.getCategories(this.typeCertPro, this.yearSelected).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.categories = res
      this.selectCategoryId = this.categories[0]?.id
      this.getQuestions()
    })
  }

  getQuestions() {
    this.showPreview = false
    this.certProService.getQuestions(this.selectCategoryId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.questions = res
    })

  }

  getCertProTypes() {
    this.entityService.getGroupsEntities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.certProTypes = res
      this.typeCertPro = res[0]
      this.getCategories()
    })
  }

  editCategory(modal, category) {
    this.newCategoryForm.patchValue(category)
    this.open(modal)
  }

  editQuestion(modal, question) {
    this.newQuestionForm.patchValue(question)
    this.newQuestionForm.patchValue({ year: this.yearSelected, categoryId: this.selectCategoryId, enabled: true })
    this.open(modal)
  }

  editSubQuestion(modal, question, subQuestion) {
    this.newSubQuestionForm.patchValue(subQuestion)
    this.newSubQuestionForm.patchValue({ parentId: question.id, year: this.yearSelected, categoryId: this.selectCategoryId, enabled: true })
    this.open(modal)
  }

  addCategory() {
    this.newCategoryForm.patchValue({
      year: this.yearSelected,
      groupId: this.typeCertPro.id,
      enabled: true
    })

    if (!this.newCategoryForm.value.id) this.categories.push(this.newCategoryForm.value)
    else {
      const index = this.categories.findIndex(x => x.id === this.newCategoryForm.value.id);
      this.categories[index] = this.newCategoryForm.value;
    }

    this.certProService.updateCategory(this.categories).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getCategories()
      this.newCategoryForm.reset()
    })
  }

  addQuestion() {
    this.newQuestionForm.patchValue({
      categoryId: this.selectCategoryId,
      year: this.yearSelected,
      enabled: true
    })

    if (!this.newQuestionForm.value.id) this.questions.push(this.newQuestionForm.value)
    else {
      const index = this.questions.findIndex(x => x.id === this.newQuestionForm.value.id);
      this.questions[index] = this.newQuestionForm.value;
    }

    this.certProService.updateQuestions(this.questions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
      this.newQuestionForm.reset()
    })
  }

  addSubQuestion() {
    const subquestion = this.newSubQuestionForm.value;
    const question = this.questions.find(x => x.id === subquestion.parentId);
    if (!question) return;

    question.subitems ??= [];

    const index = question.subitems.findIndex(x => x.id === subquestion.id);
    index > -1 ? question.subitems[index] = subquestion : question.subitems.push(subquestion);

    this.certProService.updateQuestions(this.questions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
      this.newSubQuestionForm.reset()
    })
  }


  selectCategory(id) {
    this.selectCategoryId = id
    this.getQuestions()
  }

  moveUp(i) {
    moveItemInArray(this.categories, i, i - 1)
    this.certProService.updateCategory(this.categories).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getCategories()
    })
  }

  moveDown(i) {
    moveItemInArray(this.categories, i, i + 1)
    this.certProService.updateCategory(this.categories).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getCategories()
    })
  }

  moveUpQuestion(i) {
    moveItemInArray(this.questions, i, i - 1);
    this.certProService.updateQuestions(this.questions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
    })
  }

  moveDownQuestion(i) {
    moveItemInArray(this.questions, i, i + 1)
    this.certProService.updateQuestions(this.questions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
    })
  }

  moveDownSubQuestion(question, i) {
    moveItemInArray(question.subitems, i, i + 1)
    this.certProService.updateQuestions(this.questions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
    })
  }

  moveUpSubQuestion(question, i) {
    moveItemInArray(question.subitems, i, i - 1)
    this.certProService.updateQuestions(this.questions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
    })
  }

  preventRemoveCategory(id: string) {
    Swal.fire({
      title: "¿Seguro que desea eliminar esta categoria?",
      text: "Se eliminarán todas las preguntas asociadas",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar"
    }).then((result) => {
      if (result.isConfirmed) {
        this.removeCategory(id)
      }
    })
  }

  preventRemoveQuestion(id: string) {
    Swal.fire({
      title: "¿Seguro que desea eliminar esta pregunta?",
      text: "Se eliminarán todos los criterios de evaluación asociados",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar"
    }).then((result) => {
      if (result.isConfirmed) {
        this.removeQuestion(id)
      }
    })
  }

  preventRemoveSubQuestion(id: string) {
    Swal.fire({
      title: "¿Seguro que desea eliminar este criterio de evaluación?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar"
    }).then((result) => {
      if (result.isConfirmed) {
        this.removeSubQuestion(id)
      }
    })
  }

  removeCategory(id: string) {
    this.certProService.deleteCategory(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getCategories()
    })
  }

  removeQuestion(id) {
    this.certProService.deleteQuestion(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
    })
  }

  removeSubQuestion(id: string) {
    this.certProService.deleteSubQuestion(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getQuestions()
    })
  }

  viewPreview() {
    this.showPreview = true
    this.certProService.getPreview(this.typeCertPro.id, this.yearSelected).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.preview = res
    })
  }

}
